import { useContext, useEffect, useState } from "react";
import { FormattedMessage, FormattedPlural, useIntl } from "react-intl";
import { AppContext } from 'context/AppContext';

import { ErrorMessage } from "components/ErrorMessage/ErrorMessage";
import { Spinner } from "components/spinner/Spinner";
import { setCurrencyFormat } from "utils/stringsUtils";

export const CreditCalculator = ({ credit_types, isLoading, error }) => {
	const intl = useIntl();
	const [options, setOptions] = useState([]);
	const [allOptions, setAllOptions] = useState([]);
	const [optionSelected, setOptionSelected] = useState({});
	const [fee, setFee] = useState(0);
	const [amount, setAmount] = useState(0);

	const { countryData } = useContext(AppContext);
	const { currency } = countryData;

	useEffect(() => {
		if (credit_types.length > 0) {
			const optionsForSelect = credit_types
				.filter(credit => credit.payments_quantity === 1)
				.map((credit) => {
					const quantity =
						credit.payments_quantity > 1
							? "APP.CREDIT.CALCULATOR.PAYMENTS.LABEL"
							: "APP.CREDIT.CALCULATOR.PAYMENT.LABEL";
					return {
						value: credit.uid,
						label: `${credit.period_text} - ${credit.payments_quantity} ${intl.formatMessage({ id: quantity })}`,
					};
				});
			setOptions([optionsForSelect[0]]);
			setOptionSelected(credit_types[0]);
			setAllOptions(credit_types);
		}
	}, [credit_types, intl]);

	const handleOnSubmit = (e) => {
		e.preventDefault();
	};

	const handleOnSelectChange = (e) => {
		const { value } = e.target;
		const newOptionSelected = allOptions.find((credit) => credit.uid === value);
		let newFee = 0;

		if (newOptionSelected) {
			setOptionSelected(newOptionSelected);

			if (amount > 0) {
				const interestRate = newOptionSelected.interest_rate / 100;
				const newInterest = amount * interestRate;
				newFee = (amount + newInterest) / Number(newOptionSelected.payments_quantity);
				setFee(newFee);
			}
		}
	};

	const handleAmountValueChange = (e) => {
		const { value } = e.target;
		const amountValue = Number(value);
		let newFee = 0;

		if (amountValue > 0) {
			const interestRate = optionSelected.interest_rate / 100;
			const newInterest = amountValue * interestRate;
			newFee = (amountValue + newInterest) / Number(optionSelected.payments_quantity);
		}

		setAmount(Number(value));
		setFee(newFee);
	};

	return (
		<section className="section relative overflow-hidden">
			{/* Background grid pattern */}
			<div className="absolute inset-0 bg-grid opacity-40" />
			<div className="absolute top-1/2 left-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl -translate-x-1/2" />
			<div className="absolute top-1/2 right-0 w-80 h-80 bg-accent/5 rounded-full blur-3xl translate-x-1/2" />

			<div className="container-main relative">
				{/* Section Header */}
				<div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
					<span className="badge mb-4">Calculadora</span>
					<h2 className="text-display-md md:text-display-lg text-primary mb-4">
						<FormattedMessage id="APP.CREDIT.CALCULATOR.SECTION.TITLE.2" />
					</h2>
					<p
						dangerouslySetInnerHTML={{
							__html: intl.formatMessage({
								id: "APP.CREDIT.CALCULATOR.SECTION.DESCRIPTION",
							}),
						}}
						className="text-body-lg text-surface-600"
					/>
				</div>

				{/* Calculator Card */}
				<div className="max-w-xl mx-auto">
					<form onSubmit={handleOnSubmit}>
						{/* Amount Input */}
						{!error && !isLoading && credit_types.length > 0 && (
							<div className="bg-white rounded-xl border-2 border-accent p-2 mb-4 shadow-card">
								<div className="flex items-center gap-3 px-4 py-3">
									<span className="text-2xl md:text-3xl font-semibold text-surface-400">{currency}</span>
									<input
										type="number"
										className="flex-1 text-2xl md:text-3xl font-semibold text-primary outline-none bg-transparent placeholder:text-surface-300"
										placeholder="2,000"
										min={0}
										defaultValue={amount}
										onChange={handleAmountValueChange}
									/>
									<button
										type="submit"
										className="w-12 h-12 bg-accent hover:bg-accent-dark text-white rounded-lg flex items-center justify-center transition-colors"
									>
										<svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
										</svg>
									</button>
								</div>
							</div>
						)}

						{/* Results */}
						{isLoading ? (
							<div className="bg-white rounded-xl p-8 flex justify-center items-center border border-surface-200">
								<Spinner />
							</div>
						) : (
							<>
								{error ? (
									<div className="bg-white rounded-xl p-8 border border-surface-200">
										<ErrorMessage
											image="warning.svg"
											descriptionKey="APP.GENERAL.ERROR.DESCRIPTION"
										/>
									</div>
								) : (
									<>
										{credit_types.length > 0 ? (
											<div className="bg-white rounded-xl p-6 border border-surface-200 shadow-card">
												{/* Period Selector */}
												<div className="mb-6 pb-6 border-b border-surface-100">
													<label className="text-body-sm font-medium text-surface-500 mb-2 block">
														Período de pago
													</label>
													<select
														className="w-full"
														name="period"
														value={optionSelected.uid}
														onChange={handleOnSelectChange}
													>
														{options.map((option) => (
															<option value={option.value} key={option.value}>
																{option.label}
															</option>
														))}
													</select>
												</div>

												{/* Results Grid */}
												<div className="grid grid-cols-2 gap-6">
													{/* Payments Count */}
													<div>
														<div className="text-body-sm text-surface-500 mb-1">Cantidad de pagos</div>
														<div className="text-display-sm text-primary">
															{optionSelected.payments_quantity}{" "}
															<span className="text-body-md font-normal text-surface-600">
																<FormattedPlural
																	value={optionSelected.payments_quantity}
																	one={intl.formatMessage({ id: "APP.CREDIT.CALCULATOR.QUANTITY.LABEL" })}
																	other={intl.formatMessage({ id: "APP.CREDIT.CALCULATOR.QUANTITIES.LABEL" })}
																/>
															</span>
														</div>
													</div>

													{/* Fee Amount */}
													<div>
														<div className="text-body-sm text-surface-500 mb-1">Valor de cuota</div>
														<div className="text-display-sm text-accent">
															{currency} {setCurrencyFormat(fee)}
														</div>
													</div>
												</div>

												{/* Info Note */}
												<div className="mt-6 pt-6 border-t border-surface-100">
													<p className="text-body-sm text-surface-500 flex items-start gap-2">
														<svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
														Este cálculo es estimado. El monto final puede variar según tu perfil crediticio.
													</p>
												</div>
											</div>
										) : (
											<div className="bg-white rounded-xl p-8 border border-surface-200">
												<ErrorMessage
													image="empty.svg"
													descriptionKey="APP.GENERAL.ERROR.EMPTY.DATA"
												/>
											</div>
										)}
									</>
								)}
							</>
						)}
					</form>
				</div>
			</div>
		</section>
	);
};
